import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchPatientProfiles,
  createPatientProfile,
  fetchPatientProfileById,
  updatePatientProfile,
  deletePatientProfile
} from '../../api/api_patient_profiles';

export const fetchPatientProfilesThunk = createAsyncThunk(
  'patientProfiles/fetchPatientProfiles',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await fetchPatientProfiles(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const createPatientProfileThunk = createAsyncThunk(
  'patientProfiles/createPatientProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await createPatientProfile(profileData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchPatientProfileByIdThunk = createAsyncThunk(
  'patientProfiles/fetchPatientProfileById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchPatientProfileById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const updatePatientProfileThunk = createAsyncThunk(
  'patientProfiles/updatePatientProfile',
  async ({ id, profileData }, { rejectWithValue }) => {
    try {
      const response = await updatePatientProfile(id, profileData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const deletePatientProfileThunk = createAsyncThunk(
  'patientProfiles/deletePatientProfile',
  async (id, { rejectWithValue }) => {
    try {
      await deletePatientProfile(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

const patientProfilesSlice = createSlice({
  name: 'patientProfiles',
  initialState: {
    profiles: [],
    currentProfile: null,
    pagination: null,
    loading: false,
    error: null,
    status: 'idle',
  },
  reducers: {
    clearCurrentProfile: (state) => {
      state.currentProfile = null;
    },
    setCurrentProfile: (state, action) => {
      state.currentProfile = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch patient profiles
      .addCase(fetchPatientProfilesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientProfilesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.profiles = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
        state.status = 'succeeded';
      })
      .addCase(fetchPatientProfilesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Create patient profile
      .addCase(createPatientProfileThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPatientProfileThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.profiles.unshift(action.payload.data || action.payload);
        state.status = 'succeeded';
      })
      .addCase(createPatientProfileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Fetch patient profile by ID
      .addCase(fetchPatientProfileByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientProfileByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProfile = action.payload.data || action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchPatientProfileByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Update patient profile
      .addCase(updatePatientProfileThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePatientProfileThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedProfile = action.payload.data || action.payload;
        const index = state.profiles.findIndex(p => p.id === updatedProfile.id);
        if (index !== -1) {
          state.profiles[index] = updatedProfile;
        }
        if (state.currentProfile && state.currentProfile.id === updatedProfile.id) {
          state.currentProfile = updatedProfile;
        }
        state.status = 'succeeded';
      })
      .addCase(updatePatientProfileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Delete patient profile
      .addCase(deletePatientProfileThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePatientProfileThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.profiles = state.profiles.filter(p => p.id !== action.payload);
        if (state.currentProfile && state.currentProfile.id === action.payload) {
          state.currentProfile = null;
        }
        state.status = 'succeeded';
      })
      .addCase(deletePatientProfileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      });
  },
});

export const { clearCurrentProfile, setCurrentProfile } = patientProfilesSlice.actions;
export default patientProfilesSlice.reducer;

